import { useMutation } from '@tanstack/react-query';
import { message } from 'antd';
import _axios from 'axios';

import * as adminApi from '@/apis/admin';
import type {
  AddAdminNoticeRequest,
  AddAdminUserRequest,
  AddAdminUserResponse,
  AddMarketingProductRequest,
  AddMarketingProductResponse,
  AdminNoticeResponse,
  AdminUserRole,
  GetAdminUploadUrlsResponse,
  OrderingMarketingProductRequest,
  UpdateAdminNoticeRequest,
  UpdateAdminUserRolePermissionsRequest,
  UpdateInvoiceParams,
  UpdateInvoiceResponse,
  UpdateMarketingProductRequest,
  UpdateMarketingProductResponse,
  UpdateOrderNoteParams,
  UpdateOrderNoteResponse,
  UpdateOrderStatusParams,
  UpdateOrderStatusResponse,
} from '@/types/admin';
import type { PathVariableRequestParams } from '@/types/api';
import type { CommonUseMutationOptions } from '@/types/app';
import type { UseUploadImageParams } from '@/types/upload';

export const useAddMarketingProduct = (
  options?: CommonUseMutationOptions<
    AddMarketingProductRequest,
    AddMarketingProductResponse
  >
) => {
  const mutation = useMutation({
    mutationFn: (params) => adminApi.addMarketingProduct(params),
    ...options,
  });

  return mutation;
};

export const useUpdateMarketingProduct = (
  options?: CommonUseMutationOptions<
    UpdateMarketingProductRequest,
    UpdateMarketingProductResponse
  >
) => {
  const mutation = useMutation({
    mutationFn: (params) => adminApi.updateMarketingProduct(params),
    ...options,
  });

  return mutation;
};

export const useOrderingMarketingProducts = (
  options?: CommonUseMutationOptions<OrderingMarketingProductRequest, null>
) => {
  const mutation = useMutation({
    mutationFn: (params) => adminApi.orderingMarketingProducts(params),
    ...options,
  });

  return mutation;
};

export const useUploadImage = (
  options?: Omit<
    CommonUseMutationOptions<UseUploadImageParams, GetAdminUploadUrlsResponse>,
    'onSuccess'
  >
) => {
  const mutation = useMutation({
    mutationFn: ({ files }) =>
      adminApi.getUploadUrls({
        imageCounts: files.length,
      }),
    onSuccess: async (
      { imageUrls, url, getUrl },
      { files, onError, onProgress, onSuccess, callback, that }
    ) => {
      try {
        for (let i = 0; i < imageUrls.length; i += 1) {
          const formData = new FormData();

          Object.entries(imageUrls[i]).forEach(([key, value]) => {
            formData.append(key, value as string);
          });
          formData.append('file', files[i]);

          // eslint-disable-next-line no-await-in-loop
          await _axios.post(url, formData, {
            headers: {},
            onUploadProgress: (progressEvent) => {
              if (progressEvent.total) {
                const percent = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total
                );
                onProgress?.({ percent });
              }
            },
          });

          onSuccess?.({ url: `${getUrl}${imageUrls[i].Key}` }, files[i]);
        }

        callback?.(
          that,
          imageUrls.map((item) => `${getUrl}${item.Key}`)
        );
      } catch (error) {
        console.error('Upload error:', error);
        onError?.(error as Error);
        message.error(`업로드 실패`);
      }
    },
    ...options,
  });

  return mutation;
};

export const useUpdateInvoice = (
  options?: CommonUseMutationOptions<UpdateInvoiceParams, UpdateInvoiceResponse>
) => {
  const mutation = useMutation({
    mutationFn: (params) => adminApi.updateInvoice(params),
    ...options,
  });

  return mutation;
};

export const useUpdateOrderStatus = (
  options?: CommonUseMutationOptions<
    UpdateOrderStatusParams,
    UpdateOrderStatusResponse
  >
) => {
  const mutation = useMutation({
    mutationFn: (params) => adminApi.updateOrderStatus(params),
    ...options,
  });

  return mutation;
};

export const useUpdateOrderNote = (
  options?: CommonUseMutationOptions<
    UpdateOrderNoteParams,
    UpdateOrderNoteResponse
  >
) => {
  const mutation = useMutation({
    mutationFn: (params) => adminApi.updateOrderNote(params),
    ...options,
  });

  return mutation;
};

/** Notice */
export const useAddAdminNotice = (
  options?: CommonUseMutationOptions<AddAdminNoticeRequest, AdminNoticeResponse>
) => {
  const mutation = useMutation({
    mutationFn: (params) => adminApi.addAdminNotice(params),
    ...options,
  });

  return mutation;
};

export const useUpdateAdminNotice = (
  options?: CommonUseMutationOptions<
    UpdateAdminNoticeRequest,
    AdminNoticeResponse
  >
) => {
  const mutation = useMutation({
    mutationFn: (params) => adminApi.updateAdminNotice(params),
    ...options,
  });

  return mutation;
};

export const useDeleteAdminNotice = (
  options?: CommonUseMutationOptions<PathVariableRequestParams, void>
) => {
  const mutation = useMutation({
    mutationFn: (params) => adminApi.deleteAdminNotice(params),
    ...options,
  });

  return mutation;
};

/** User */
export const useAddAdminUser = (
  options?: CommonUseMutationOptions<AddAdminUserRequest, AddAdminUserResponse>
) => {
  const mutation = useMutation({
    mutationFn: (params) => adminApi.addAdminUser(params),
    ...options,
  });

  return mutation;
};

export const useUpdateAdminUserRolePermissions = (
  options?: CommonUseMutationOptions<
    UpdateAdminUserRolePermissionsRequest,
    AdminUserRole
  >
) => {
  const mutation = useMutation({
    mutationFn: (params) => adminApi.updateAdminUserRolePermissions(params),
    ...options,
  });

  return mutation;
};
